/**
 * The code-drawn placeholder captain portrait (M6 plan P32, §9; spec §5): a seeded head-and-shoulders silhouette,
 * the captain's initials and a faction tint, drawn in a 64 × 64 SVG until the ~40 portrait pool exists (D-028).
 *
 * The seed is the captain's name and portrait id (`CaptainSummary.portrait`, 0..39), which a captain keeps from the
 * roster through hire to the memorial (the `cand-<k>` / `cap-<seq>` ids change on hire), so a captain always has
 * the same face. The tint is the origin faction's (captainsScreen.json `tints`); the memorial draws every face in
 * its quiet grey. Pure.
 */
import { createRng, range, type Rng } from '../../../core/rng'

export interface PortraitTint {
  bg: string
  fg: string
}

/** Hair silhouettes the seed picks from. */
export const HAIR_STYLES = ['none', 'crop', 'long', 'crest', 'knot'] as const
export type HairStyle = (typeof HAIR_STYLES)[number]

/** Collar silhouettes the seed picks from. */
export const COLLAR_STYLES = ['plain', 'high', 'vee'] as const
export type CollarStyle = (typeof COLLAR_STYLES)[number]

/** Everything the Portrait component draws (viewBox 0 0 64 64). */
export interface PortraitRecipe {
  initials: string
  bg: string
  fg: string
  head: { cx: number; cy: number; rx: number; ry: number }
  /** The shoulders and neck, a closed path. */
  shoulders: string
  hair: HairStyle
  /** The hair's path ('' for none). */
  hairPath: string
  collar: CollarStyle
  /** The collar's stroke path ('' for plain). */
  collarPath: string
}

/** FNV-1a 32-bit hash of `s`. */
export function hashSeed(s: string): number {
  let h = 0x811c9dc5
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return h >>> 0
}

/** "Ilse Varga" → "IV", "Oko" → "O", "" → "?". The first and last words' first letters. */
export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter((w) => w.length > 0)
  if (words.length === 0) return '?'
  const first = words[0].charAt(0)
  const last = words.length > 1 ? words[words.length - 1].charAt(0) : ''
  return (first + last).toUpperCase()
}

/** One decimal, so the paths are short and stable. */
function r1(n: number): number {
  return Math.round(n * 10) / 10
}

function pick<T>(rng: Rng, list: readonly T[]): T {
  return list[Math.min(list.length - 1, Math.floor(range(rng, 0, list.length)))]
}

function hairPath(style: HairStyle, cx: number, cy: number, rx: number, ry: number): string {
  const top = r1(cy - ry)
  const l = r1(cx - rx)
  const r = r1(cx + rx)
  switch (style) {
    case 'none':
      return ''
    case 'crop':
      return `M${r1(l - 1)} ${r1(cy - 1)} Q${r1(cx)} ${r1(top - 6)} ${r1(r + 1)} ${r1(cy - 1)} Q${r1(cx)} ${r1(top + 3)} ${r1(l - 1)} ${r1(cy - 1)}Z`
    case 'long':
      return `M${r1(l - 2)} ${r1(cy + ry * 0.9)} L${r1(l - 2)} ${r1(cy - 2)} Q${r1(cx)} ${r1(top - 7)} ${r1(r + 2)} ${r1(cy - 2)} L${r1(r + 2)} ${r1(cy + ry * 0.9)} L${r} ${r1(cy + ry * 0.9)} L${r} ${r1(cy - 1)} Q${r1(cx)} ${r1(top + 2)} ${l} ${r1(cy - 1)} L${l} ${r1(cy + ry * 0.9)}Z`
    case 'crest':
      return `M${r1(cx - 2.5)} ${r1(top + 3)} L${r1(cx)} ${r1(top - 6)} L${r1(cx + 2.5)} ${r1(top + 3)}Z`
    case 'knot':
      return `M${r1(cx + rx * 0.3)} ${r1(top + 1)} a4 4 0 1 1 ${r1(rx * 0.5)} -6 a4 4 0 1 1 -${r1(rx * 0.5)} 6Z`
  }
}

function collarPath(style: CollarStyle, cx: number, neckY: number, neckW: number): string {
  switch (style) {
    case 'plain':
      return ''
    case 'high':
      return `M${r1(cx - neckW - 3)} ${r1(neckY + 2)} L${r1(cx + neckW + 3)} ${r1(neckY + 2)}`
    case 'vee':
      return `M${r1(cx - neckW - 2)} ${r1(neckY + 1)} L${r1(cx)} ${r1(neckY + 9)} L${r1(cx + neckW + 2)} ${r1(neckY + 1)}`
  }
}

/** The portrait of the captain named `name` with portrait id `portrait`, tinted `tint`. Deterministic. */
export function portraitRecipe(name: string, portrait: number, tint: PortraitTint): PortraitRecipe {
  const rng = createRng(hashSeed(`${name}|${portrait}`))
  const cx = r1(range(rng, 30.5, 33.5))
  const cy = r1(range(rng, 23, 27))
  const rx = r1(range(rng, 8.5, 11.5))
  const ry = r1(range(rng, 10.5, 13.5))
  const shoulderY = r1(range(rng, 45, 51))
  const halfWidth = r1(range(rng, 21, 28))
  const neckW = r1(range(rng, 4, 6))
  const neckY = r1(cy + ry - 1)
  const hair = pick(rng, HAIR_STYLES)
  const collar = pick(rng, COLLAR_STYLES)
  const shoulders =
    `M${r1(cx - halfWidth)} 64 Q${r1(cx - halfWidth)} ${shoulderY} ${r1(cx - neckW - 6)} ${r1(shoulderY - 3)}` +
    ` L${r1(cx - neckW)} ${neckY} L${r1(cx + neckW)} ${neckY} L${r1(cx + neckW + 6)} ${r1(shoulderY - 3)}` +
    ` Q${r1(cx + halfWidth)} ${shoulderY} ${r1(cx + halfWidth)} 64Z`
  return {
    initials: initialsOf(name),
    bg: tint.bg,
    fg: tint.fg,
    head: { cx, cy, rx, ry },
    shoulders,
    hair,
    hairPath: hairPath(hair, cx, cy, rx, ry),
    collar,
    collarPath: collarPath(collar, cx, neckY, neckW),
  }
}
